import { type FormEvent, useEffect, useState } from "react";
import { useParams } from "react-router";

import {
  type DecisionResult,
  type MappingDecision,
  type ProposalView,
  type ProposedTarget,
  type TargetCandidate,
  approveProposal,
  editProposal,
  getProposal,
  message,
  rejectProposal,
} from "./studioClient";

function text(form: FormData, name: string): string {
  const value = form.get(name);
  return typeof value === "string" ? value.trim() : "";
}

function lines(form: FormData, name: string): string[] {
  return text(form, name)
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
}

function decisionFrom(form: FormData): MappingDecision {
  const clean = text(form, "clean");
  const edge = text(form, "edge");
  return {
    rule_key: text(form, "rule_key"),
    scope_level: "engagement",
    context: { site: text(form, "site") },
    owner_party: text(form, "owner"),
    authority: text(form, "authority"),
    proof_limits: lines(form, "proof_limits"),
    test_cases: [
      { case_kind: "clean", description: clean, input: { text: clean }, expected: { accepted: true } },
      { case_kind: "edge", description: edge, input: { text: edge }, expected: { review: true } },
    ],
    revalidation_triggers: lines(form, "revalidation"),
    rollback_path: text(form, "rollback"),
    reason: text(form, "reason"),
  };
}

function citation(metadata: TargetCandidate): string {
  const profile = metadata.profile_reference;
  if (profile) return `${profile.profile_version} ${profile.section} ${profile.row_key}`;
  const pack = metadata.pack_reference;
  return pack ? `${pack.pack_key} ${pack.pack_version}` : "No reference";
}

function TargetColumn({
  title,
  target,
  metadata,
}: {
  title: string;
  target: ProposedTarget;
  metadata: TargetCandidate | undefined;
}) {
  const name = `${target.target_object}.${target.target_attribute}`;
  return (
    <article aria-label={`${title}: ${name}`}>
      <h3>
        {title}: {name}
      </h3>
      <p>{target.reasoning}</p>
      {target.transformation && <p>Transformation: {target.transformation}</p>}
      {target.known_limits.length > 0 && <p>Known limits: {target.known_limits.join("; ")}</p>}
      {metadata ? (
        <dl>
          <dt>Title</dt>
          <dd>{metadata.title ?? "Not stated"}</dd>
          <dt>Type and length</dt>
          <dd>
            {metadata.maxtype ?? "Unknown"} {metadata.length ?? ""}
          </dd>
          <dt>Required</dt>
          <dd>{String(metadata.required)}</dd>
          <dt>Persistent</dt>
          <dd>{String(metadata.persistent)}</dd>
          <dt>Domain</dt>
          <dd>{metadata.domainid ?? "None"}</dd>
          <dt>Scope</dt>
          <dd>{metadata.scope ?? "Unknown"}</dd>
          <dt>Destination</dt>
          <dd>
            {metadata.destination}
            {metadata.unverified_reason ? ` (${metadata.unverified_reason})` : ""}
          </dd>
          <dt>Cited from</dt>
          <dd>{citation(metadata)}</dd>
        </dl>
      ) : (
        <p>No target metadata was recorded for this target.</p>
      )}
    </article>
  );
}

/** One proposal, its alternatives and target metadata side by side, and the decision form. */
export function ProposalPage() {
  const { proposalId = "" } = useParams();
  const [view, setView] = useState<ProposalView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<DecisionResult | null>(null);

  useEffect(() => {
    let live = true;
    getProposal(proposalId)
      .then((found) => {
        if (live) setView(found);
      })
      .catch((caught: unknown) => {
        if (live) setError(message(caught));
      });
    return () => {
      live = false;
    };
  }, [proposalId, done]);

  function act(run: () => Promise<DecisionResult>) {
    setError(null);
    run()
      .then(setDone)
      .catch((caught: unknown) => {
        setError(message(caught));
      });
  }

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const body = decisionFrom(form);
    const target_object = text(form, "target_object");
    const target_attribute = text(form, "target_attribute");
    act(() =>
      target_object && target_attribute
        ? editProposal(proposalId, { target_object, target_attribute }, body)
        : approveProposal(proposalId, body),
    );
  }

  function onReject(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const reason = text(new FormData(event.currentTarget), "reject_reason");
    act(() => rejectProposal(proposalId, reason));
  }

  if (!view) {
    return error ? <p role="alert">Could not load the proposal: {error}</p> : <p>Loading.</p>;
  }
  const { proposal, observation, decision, mapping, support } = view;
  const metadataFor = (target: ProposedTarget) =>
    proposal.candidate_targets.find(
      (c) => c.objectname === target.target_object && c.attributename === target.target_attribute,
    );
  return (
    <section aria-labelledby="proposal-title">
      <h1 id="proposal-title">Mapping proposal</h1>
      {error && <p role="alert">The decision was refused: {error}</p>}
      <h2>Source</h2>
      <p>
        {observation.label ?? "Unlabelled"}: {observation.transcribed_text ?? ""}{" "}
        {observation.unit_text ?? ""} (region {observation.region_key})
      </p>
      <p>
        Business concept:{" "}
        {proposal.concept
          ? `${proposal.concept.name} (${proposal.concept.key} v${String(proposal.concept.version)})`
          : "No certified concept"}
      </p>
      <p>Outcome: {proposal.outcome}</p>
      {proposal.explanation && <p>{proposal.explanation}</p>}
      {proposal.repair_attempts > 0 && (
        <p>Repair attempts on the model&apos;s output: {proposal.repair_attempts}</p>
      )}
      {proposal.missing_context.length > 0 && (
        <p>Missing context: {proposal.missing_context.join("; ")}</p>
      )}
      {proposal.existing_mappings.length > 0 && (
        <ul aria-label="Existing mappings">
          {proposal.existing_mappings.map((m) => (
            <li key={m.rule_key}>
              {m.rule_key}: {m.target}, {m.support_level},{" "}
              {m.applies ? "covers this observation" : "does not apply here"}
            </li>
          ))}
        </ul>
      )}
      <h2>Targets</h2>
      <div role="group" aria-label="Proposal and alternatives" style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
        {proposal.proposed && (
          <TargetColumn
            title="Proposed"
            target={proposal.proposed}
            metadata={metadataFor(proposal.proposed)}
          />
        )}
        {proposal.alternatives.map((alternative) => (
          <TargetColumn
            key={alternative.target_attribute}
            title="Alternative"
            target={alternative}
            metadata={metadataFor(alternative)}
          />
        ))}
      </div>
      {proposal.no_alternative_statement && <p>{proposal.no_alternative_statement}</p>}
      {proposal.rejected_targets.length > 0 && (
        <ul aria-label="Rejected targets">
          {proposal.rejected_targets.map((r) => (
            <li key={`${r.target_object}.${r.target_attribute}`}>
              {r.target_object}.{r.target_attribute}: {r.reason}
            </li>
          ))}
        </ul>
      )}
      {proposal.reliability_references.length > 0 && (
        <p>
          Reliability references, for reading the source only:{" "}
          {proposal.reliability_references.join(", ")}
        </p>
      )}
      <h2>Decision</h2>
      {decision ? (
        <p data-testid="decision">
          {decision.decision} by {decision.decided_by}: {decision.reason}
        </p>
      ) : (
        <>
          <form onSubmit={onSubmit} aria-label="Approve or edit">
            <label>
              Rule key <input name="rule_key" required pattern="[a-z0-9][a-z0-9._\-]{1,126}" />
            </label>
            <label>
              Site scope <input name="site" required />
            </label>
            <label>
              Owner <input name="owner" required />
            </label>
            <label>
              Source authority <input name="authority" required />
            </label>
            <label>
              Proof limits, one per line <textarea name="proof_limits" required />
            </label>
            <label>
              Clean test case <input name="clean" required />
            </label>
            <label>
              Edge test case <input name="edge" required />
            </label>
            <label>
              Revalidation triggers, one per line <textarea name="revalidation" required />
            </label>
            <label>
              Rollback path <input name="rollback" required />
            </label>
            <label>
              Reason <input name="reason" required />
            </label>
            <fieldset>
              <legend>Change the target (leave empty to approve as proposed)</legend>
              <label>
                Object <input name="target_object" />
              </label>
              <label>
                Attribute <input name="target_attribute" />
              </label>
            </fieldset>
            <p>
              Approving activates the mapping. If you change the target, you become its proposer
              and a second Data Mapper must activate it.
            </p>
            <button type="submit">Save decision</button>
          </form>
          <form onSubmit={onReject} aria-label="Reject">
            <label>
              Why reject <input name="reject_reason" required />
            </label>
            <button type="submit">Reject proposal</button>
          </form>
        </>
      )}
      {done?.awaiting_second_approver && (
        <p role="status">Saved. A second Data Mapper must activate this mapping.</p>
      )}
      {mapping && (
        <p data-testid="mapping">
          {mapping.target} version {mapping.version}: {mapping.lifecycle_state}
          {support ? `, ${support.level}: ${support.reason}` : ""}
        </p>
      )}
    </section>
  );
}
